/**
 * HospitalCard — one hospital's current state in the live overview.
 *
 * Composes the rank, name, people-waiting count, a congestion chip, the triage
 * mix, and a freshness badge. Renders a clearly-labelled no-data state when the
 * hospital has not reported (just seeded, or an outage). The whole card links to
 * the hospital's detail screen (`/ps/:slug`).
 *
 * `idx` is the 0-based display index, used to stagger the reveal animation so
 * cards cascade in rather than all appearing at once.
 */

import { useRef, useState, type MouseEvent } from "react";
import { Link } from "react-router-dom";
import type { RankedHospital } from "@/lib/congestion";
import { CONGESTION_LABEL } from "@/lib/congestion";
import { beatDuration, beatDurationStyle, beatPhaseStyle } from "@/lib/busyness";
import { isMorphing, useMorphNavigate } from "@/lib/morph";
import { HeartMark } from "./HeartMark";
import { TriageMix } from "./TriageMix";
import { FreshnessBadge } from "./FreshnessBadge";

interface HospitalCardProps {
  ranked: RankedHospital;
  /** 0-based display index — drives the staggered reveal animation delay. */
  idx: number;
  /** Injectable clock for deterministic tests; defaults to the real now. */
  now?: Date | undefined;
}

export function HospitalCard({ ranked, idx, now }: HospitalCardProps) {
  const { hospital, rank, level } = ranked;
  const waitingLabel = hospital.totalWaiting === 1 ? "persona in attesa" : "persone in attesa";
  const beatDur = beatDuration(hospital.totalWaiting, level);

  const articleRef = useRef<HTMLElement>(null);
  const morph = useMorphNavigate();
  // Freeze at mount: when this overview was re-mounted BY a back-morph, skip the
  // one-shot rise-in (the returning card is a shared element — a rise would make
  // it jump after the morph settles).
  const [arrivedViaMorph] = useState(() => isMorphing());

  // Progressive enhancement: morph the clicked card into the detail hero. Left
  // click, unmodified, on a card that has data (no-data cards have no shared
  // heart/number to morph). Otherwise let the <Link> navigate normally.
  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (!morph.enabled || !hospital.hasData) return;
    e.preventDefault();
    morph.toDetail(hospital.slug, articleRef.current, {
      total: hospital.totalWaiting,
      nameIt: hospital.nameIt,
      nameDe: hospital.nameDe,
      measuredAt: hospital.measuredAt,
    });
  };

  return (
    <Link
      to={`/ps/${hospital.slug}`}
      className="hospital-card-link"
      aria-label={`Dettaglio ${hospital.nameIt}`}
      onClick={handleClick}
    >
      <article
        ref={articleRef}
        data-morph-card={hospital.slug}
        className={`hospital-card hospital-card--${level}${arrivedViaMorph ? "" : " reveal"}`}
        style={{
          animationDelay: `${0.04 + idx * 0.06}s`,
          ...beatDurationStyle(beatDur),
          ...beatPhaseStyle(hospital.slug),
        }}
        aria-labelledby={`h-${hospital.slug}`}
      >
        <header className="hospital-card__head">
          <div className="hospital-card__title">
            {rank !== null && (
              <span className="hospital-card__rank" aria-label={`Posizione ${rank} per minor coda`}>
                {rank}
              </span>
            )}
            <div>
              <h3 id={`h-${hospital.slug}`} className="hospital-card__name" data-morph="name">
                {hospital.nameIt}
              </h3>
              <p className="hospital-card__name-de">{hospital.nameDe}</p>
            </div>
          </div>
          <span className={`level-chip level-chip--${level}`}>{CONGESTION_LABEL[level]}</span>
        </header>

        {hospital.hasData ? (
          <>
            <div className="hospital-card__metric">
              <span
                className="live-mark-wrap live-ring"
                style={beatDurationStyle(beatDur)}
                data-morph="heart"
              >
                <HeartMark size={14} className="heart-mark" />
              </span>
              <p className="hospital-card__total">
                <span className="hospital-card__total-value tnum" data-morph="number">
                  {hospital.totalWaiting}
                </span>
                <span className="hospital-card__total-unit">{waitingLabel}</span>
              </p>
            </div>
            <TriageMix triage={hospital.triage} total={hospital.totalWaiting} />
            <footer className="hospital-card__foot">
              <FreshnessBadge iso={hospital.measuredAt} now={now} />
            </footer>
          </>
        ) : (
          <p className="hospital-card__nodata">
            Nessun dato disponibile al momento — in attesa della prossima rilevazione.
          </p>
        )}
      </article>
    </Link>
  );
}
